import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StorefrontContext } from '../../../../../Context/StorefrontContext';
import { addAboutItemToLast, pushToVersions, setSectionFormType, updateVersionIndex } from '../../../../../Features/storefrontSlice';
import FeatureSection from './FeatureSection';
import CrossGray from '../../../../../Assets/StorefrontIcons/CrossGray.svg';
import { v4 as uuidv4 } from 'uuid';
import imageCompression from 'browser-image-compression';
import { getMediaUrl } from '../../../../../lib/utils/utils';

function FeaturesOneForm() {
    const dispatch = useDispatch();

    const selectedFeatureOneSection = useSelector((state) => state.storefront.selectedFeatureOneSection);
    const versionStack = useSelector((state) => state.storefront.versionStack);
    const versionIndex = useSelector((state) => state.storefront.versionIndex);

    const {
        defaultFeaturesOne,
        featuresOne,
        setFeaturesOne,
    } = useContext(StorefrontContext);

    function closeForm() {
        dispatch(setSectionFormType(null));
        // dispatch(setSelectedCarouselBannerSection('Banner 1'));
        setFeaturesOne(defaultFeaturesOne);
    }

    async function handleSave() {
        const currentVersion = versionStack[versionIndex];

        const newFeatureData = await Promise.all(featuresOne
            .filter(feature => feature.image && (feature.image.file || feature.image.url))
            .map(async feature => {
                const object = {};

                object.altText = feature.altText || "";
                object.Heading = feature.heading || "";
                object.Description = feature.description || "";

                const image = feature.image.file;

                if (image) {
                    let url;
        
                    if (image.size > 1048576) {
                        try {
                            const compressedBlob = await imageCompression(image, { maxSizeMB: 1, maxWidthOrHeight: 1920, useWebWorker: true });
                            const compressedFile = new File([compressedBlob], image.name, { type: image.type, lastModified: Date.now() });

                            url = await getMediaUrl(compressedFile);
                        }
                        catch (err) {
                            console.warn('Image compression failed, using original file:', err);
                            url = await getMediaUrl(image);
                        }
                    }
                    else {
                        url = await getMediaUrl(image);
                    }
                    object.imageLink = url;
                }
                else {
                    object.imageLink = feature.image.url || "";
                }
        
                return { ...object };
            })
        )

        const tempAboutData = [ ...currentVersion.storeAbout ];

        const featureOneDataIndex = tempAboutData.findIndex(
            item => item.item.TypeData === 'FEATURE1'
        );

        if (featureOneDataIndex !== -1) {
            tempAboutData[featureOneDataIndex] = {
                ...tempAboutData[featureOneDataIndex],
                item: {
                    ...tempAboutData[featureOneDataIndex].item,
                    featureItems: newFeatureData,
                }
            };

            const newIndex = versionStack[versionStack.length - 1].index + 1;

            const newVersion = {
                ...currentVersion,
                index: newIndex,
                storeAbout: tempAboutData,
                comment: 'Update Feature1 section',
            }

            dispatch(pushToVersions(newVersion));
            dispatch(updateVersionIndex(newIndex));
        }
        else {
            const object = {
                TypeData: 'FEATURE1',
                featureItems: newFeatureData
            }

            dispatch(addAboutItemToLast(object));
        }
        closeForm();
    }

    function handleAddMore() {
        const listLength = featuresOne.length;
        setFeaturesOne([
            ...featuresOne,
            {
                title: `Feature ${listLength + 1}`,
                image: {},
                altText: '',
                heading: '',
                description: '',
            }
        ]);
    }

    useEffect(() => {
        function fetchData() {
            const tempVersion = versionStack[versionIndex]?.storeAbout;

            if(tempVersion.length > 0) {
                const { item: featureOneData = null } = tempVersion.find(item => item.item.TypeData === 'FEATURE1') || {};

                if(featureOneData) {
                    if(featuresOne.length <= featureOneData.featureItems.length) {
                        let itemsToAdd = [];
                        for(let i = 0; i < featureOneData.featureItems.length; i++) {
                            itemsToAdd.push({
                                title: `Feature ${i + 1}`,
                                image: {
                                    id: uuidv4(),
                                    url: featureOneData.featureItems[i].imageLink,
                                    file: null,
                                },
                                altText: featureOneData.featureItems[i].altText,
                                heading: featureOneData.featureItems[i].Heading,
                                description: featureOneData.featureItems[i].Description,
                            })
                        }

                        setFeaturesOne(itemsToAdd);
                    }
                }
            }
        }

        fetchData();
        // eslint-disable-next-line
    }, [versionStack, versionIndex]);

    return (
        <div className='w-[305px] h-full bg-[#FFFFFF] border-l border-[#EAEAEA] flex flex-col gap-4 select-none relative'>
            <div className="flex items-center justify-between">
                <p className='font-semibold text-[20px] leading-6 text-[#121212] pl-3'>Features 1</p>

                <div className="size-11 p-[10px] flex items-center justify-center">
                    <button onClick={closeForm} className='size-6 flex items-center justify-center outline-none'>
                        <img src={CrossGray} alt="" />
                    </button>
                </div>
            </div>

            <div className="h-[calc(100%-44px)] flex flex-col overflow-y-scroll no-scrollbar pb-16">
                {
                    featuresOne.map((feature, index) => {
                        const isSelected = selectedFeatureOneSection === feature.title;
                        const isFirst = index === 0;

                        return (
                            <FeatureSection 
                                key={feature.title} 
                                feature={feature} 
                                isSelected={isSelected} 
                                isFirst={isFirst} 
                            />
                        )
                    })
                }

                <button onClick={handleAddMore} className='outline-none p-3 font-semibold text-[14px] leading-4 text-[#1055EB] w-fit'>Add more</button>
            </div>

            <div className="absolute bottom-0 left-0 w-full p-3 bg-[#FFFFFF]">
                <button onClick={handleSave} className='w-full outline-none px-4 py-[6px] bg-primaryColor hover:bg-primaryAccent rounded-md font-semibold text-[12px] leading-6 text-buttonText'>Save changes</button>
            </div>
        </div>
    );
}

export default FeaturesOneForm;
